import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useContractDetail } from '../hooks/useContractDetail';
import { useContractActivationOption } from '../hooks/useContractActivationOption';
import { ContractCard } from './ContractCard';
import { ContractList } from './ContractList';
import { CONTRACT_HISTORY_VISUAL } from '../config/contractTokens';
import theme from '../../../theme/theme';

type EmployeeContractsProps = {
  employeeId: string;
  /**
   * Employee activation flag (`statutactive='O'`). Combined with the installation
   * "Contract activation" option (`global.contratclosing`), it drives the same
   * current-contract status badge shown in the employee list.
   */
  isActivated: boolean;
};

/**
 * Contract section for the employee detail page.
 *
 * Fetches sequentially:
 *   1. GET /employees/:id/contracts/current  (via useContractDetail)
 *   2. GET /employees/:id/contracts          (via ContractList, mounts after step 1 resolves)
 *
 * Clicking a row in the history list sets selectedContractId, which triggers
 * GET /employees/:id/contracts/:contractId. Previously-fetched contracts are
 * served from the useContractDetail cache — no redundant network calls.
 */
export function EmployeeContracts({ employeeId, isActivated }: EmployeeContractsProps) {
  const { t, i18n } = useTranslation('contract');
  const lang = i18n.language;

  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  const { contract, isLoading, error, retry } = useContractDetail(
    employeeId,
    selectedContractId,
    lang,
  );

  // Installation-level "Contract activation" option (`global.contratclosing='O'`), read from
  // the tab's own route (`employee_record.contract`), not from the list's filter options.
  // `false` until it resolves.
  const activationOptionEnabled = useContractActivationOption() ?? false;

  const historyToolbar = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: theme.avatar.md,
          height: theme.avatar.md,
          bgcolor: CONTRACT_HISTORY_VISUAL.iconBg,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <HistoryIcon
          sx={{ fontSize: theme.iconSize.sm, color: CONTRACT_HISTORY_VISUAL.iconColor }}
        />
      </Box>
      <Typography component="h2" variant="subtitle1" sx={{ color: theme.palette.text.body }}>
        {t('history.title')}
      </Typography>
    </Box>
  );

  return (
    <Box>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && error && (
        <Box sx={{ p: 3 }}>
          <Alert
            severity="error"
            action={
              <Button size="small" onClick={retry}>
                {t('errors.retry')}
              </Button>
            }
          >
            {t('errors.loadError')}
          </Alert>
        </Box>
      )}

      {!isLoading && !error && !contract && (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">{t('errors.noContract')}</Alert>
        </Box>
      )}

      {!isLoading && (
        <ContractList
          employeeId={employeeId}
          onSelect={setSelectedContractId}
          // The row shown in bold follows the contract whose details are
          // displayed below (the current contract by default, or the clicked
          // row) — not the current-contract status.
          selectedContractId={contract?.hc_uuid_key ?? null}
          lang={lang}
          toolbar={historyToolbar}
          activationOptionEnabled={activationOptionEnabled}
          isActivated={isActivated}
        />
      )}

      {!isLoading && !error && contract && (
        <ContractCard
          contract={contract}
          activationOptionEnabled={activationOptionEnabled}
          isActivated={isActivated}
        />
      )}
    </Box>
  );
}
